/*
 * Copyright (C) 2018 - 2023 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React from 'react';
import {
  Alert,
  Button,
  Col,
  Collapse,
  Descriptions,
  Empty,
  Modal,
  notification,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from 'antd';
import axios from 'axios';
import { handleApiError } from '../../../../../../../../services/utils/errorHandler';
import { withConfigContext } from '../../../../../../../../components/ConfigContext';
import moment from 'moment';
import 'moment-timezone';
import EditOperationStatus from './../EditOperationStatus';
import styles from './styles.module.css';
import {
  BarsOutlined,
  CodeOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import Filter from '../../../../../../components/Filter';
import JSONPretty from 'react-json-pretty';
import { withTranslation } from 'react-i18next';

const { Panel } = Collapse;
const { TabPane } = Tabs;
const jsonTheme = {
  main: 'line-height:1.3;color:#d8d8d8;overflow:auto;',
  error: 'line-height:1.3;color:#d8d8d8;overflow:auto;',
  key: 'color:#007b98;',
  string: 'color:#656565;',
  value: 'color:#7cb304;',
  boolean: 'color:#ffa718;',
};

class DeviceOperationLog extends React.Component {
  constructor(props) {
    super(props);
    this.config = this.props.context;
    this.state = {
      operations: [],
      pagination: {},
      loading: false,
      selectedRows: [],
      searchFields: [],
      isModalVisible: false,
    };
    const { t } = this.props;
    this.columns = [
      {
        title: t('label_operationCode'),
        dataIndex: 'code',
        key: 'code',
      },
      {
        title: t('label_created'),
        dataIndex: 'createdTimeStamp',
        key: 'createdTimeStamp',
        align: 'center',
        render: createdTimeStamp => {
          return (
            <Tooltip
              title={moment
                .utc(createdTimeStamp)
                .tz(moment.tz.guess())
                .format('LLLL')}
            >
              <Tag>
                {moment
                  .utc(createdTimeStamp)
                  .tz(moment.tz.guess())
                  .fromNow()}
              </Tag>
            </Tooltip>
          );
        },
      },
      {
        title: t('label_received'),
        dataIndex: 'receivedTimeStamp',
        key: 'receivedTimeStamp',
        align: 'center',
        render: receivedTimeStamp => {
          return (
            <Tooltip
              title={moment
                .utc(receivedTimeStamp)
                .tz(moment.tz.guess())
                .format('LLLL')}
            >
              <Tag>
                {moment
                  .utc(receivedTimeStamp)
                  .tz(moment.tz.guess())
                  .fromNow()}
              </Tag>
            </Tooltip>
          );
        },
      },
      {
        title: t('label_status'),
        dataIndex: 'status',
        key: 'status',
        align: 'center',
        render: (status, row) => {
          if (status === 'COMPLETED' || status === 'ERROR') {
            let color;
            let backGroundColor;
            switch (status) {
              case 'COMPLETED':
                backGroundColor = '#bde4f2';
                color = '#007279';
                break;
              default:
                backGroundColor = '#ff9999';
                color = '#821212';
                break;
            }
            return (
              <span
                style={{
                  display: 'inline-block',
                  width: 120,
                  textAlign: 'center',
                  color: color,
                  background: backGroundColor,
                  padding: '3px 10px 3px 10px',
                  borderRadius: 7,
                }}
              >
                {t('label_uppercase_string', {
                  label: t(`label_${status.toLowerCase()}`),
                })}
              </span>
            );
          }
          return (
            <EditOperationStatus
              status={status}
              updateOperationState={this.updateOperationState}
              deviceType={this.props.deviceType}
              deviceIdentifier={this.props.deviceIdentifier}
              operationId={row.id}
            />
          );
        },
      },
    ];
  }

  componentDidMount() {
    this.getDeviceOperations();
    const { deviceType, t } = this.props;
    let operationCodes = [];
    if (deviceType === 'android') {
      operationCodes = this.config.operationCodes.androidOC;
    }
    if (deviceType === 'windows') {
      operationCodes = this.config.operationCodes.windowsOC;
    }
    if (deviceType === 'ios') {
      operationCodes = this.config.operationCodes.iosOC;
    }
    const fields = [
      {
        name: 'operationCode',
        label: t('label_operationCode'),
        placeholder: t('label_operationCode'),
        type: 'multipleSelect',
        values: operationCodes,
      },
      {
        name: 'operationStatus',
        label: t('label_operationStatus'),
        placeholder: t('label_operationStatus'),
        type: 'multipleSelect',
        values: Object.keys(this.config.operationStatus).map(
          key => this.config.operationStatus[key],
        ),
      },
      {
        name: 'createdDay',
        label: t('label_createdDate'),
        placeholder: t('label_createdDate'),
        type: 'calender',
      },
      {
        name: 'updatedDay',
        label: t('label_updatedDate'),
        placeholder: t('label_updatedDate'),
        type: 'calender',
      },
    ];
    this.setState({
      searchFields: fields,
    });
  }

  calenderDates = (dayFrom, dayTo, propertyName) => {
    let today = new Date();
    let operationFilters = '';
    let date =
      today.getMonth() + 1 + '-' + today.getDate() + '-' + today.getFullYear();
    let millisecondsTo, toDate;
    let fromDate = new Date(dayFrom);
    let millisecondsFrom = fromDate.getTime() / 1000;
    if (date === dayTo) {
      millisecondsTo = today
        .getTime()
        .toString()
        .slice(0, -3);
    } else {
      toDate = new Date(dayTo);
      millisecondsTo = toDate.getTime() / 1000;
    }
    if (propertyName === 'createdDay') {
      operationFilters =
        operationFilters +
        '&createdFrom=' +
        millisecondsFrom +
        '&createdTo=' +
        millisecondsTo;
      return operationFilters;
    }
    if (propertyName === 'updatedDay') {
      operationFilters =
        operationFilters +
        '&updatedFrom=' +
        millisecondsFrom +
        '&updatedTo=' +
        millisecondsTo;
      return operationFilters;
    }
  };

  filterValues = '';

  fetchOperationFilters = (params, values) => {
    let operationFilters = '',
      dayFrom,
      dayTo;

    if (values.hasOwnProperty('operationStatus')) {
      for (let i = 0; i < values.operationStatus.length; i++) {
        operationFilters =
          operationFilters + '&operationStatus=' + values.operationStatus[i];
      }
    }
    if (values.hasOwnProperty('operationCode')) {
      for (let i = 0; i < values.operationCode.length; i++) {
        operationFilters =
          operationFilters + '&operationCode=' + values.operationCode[i];
      }
    }

    for (let property in values) {
      if (property === 'createdDay') {
        dayFrom = moment(values.createdDay[0]).format('MM-DD-YYYY');
        dayTo = moment(values.createdDay[1]).format('MM-DD-YYYY');
        operationFilters =
          operationFilters + this.calenderDates(dayFrom, dayTo, property);
      }
      if (property === 'updatedDay') {
        dayFrom = moment(values.updatedDay[0]).format('MM-DD-YYYY');
        dayTo = moment(values.updatedDay[1]).format('MM-DD-YYYY');
        operationFilters =
          operationFilters + this.calenderDates(dayFrom, dayTo, property);
      }
    }
    this.filterValues = operationFilters;
    this.setState({ pagination: { current: 1 } });
    this.getDeviceOperations(
      {
        page: 1,
      },
      operationFilters,
    );
  };

  // API call to get activity details
  modifyOperationData = record => {
    const { t } = this.props;
    axios
      .get(
        window.location.origin +
          this.config.serverConfig.invoker.uri +
          this.config.serverConfig.invoker.deviceMgt +
          `/activities/${record.activityId}/${this.props.deviceType}/${this.props.deviceIdentifier}`,
      )
      .then(res => {
        if (res.status === 200) {
          let operations = { ...this.state.operations };
          let operationIndex = operations.operations.findIndex(
            x => x.activityId === res.data.data.activityId,
          );
          operations.operations[operationIndex].responses =
            res.data.data.activityStatus[0].responses;
          this.setState({
            operations: operations,
          });
        }
      })
      .catch(error => {
        handleApiError(error, t('api_getOperationError'), t);
      });
  };

  getDeviceOperations = (params = {}, filters = '') => {
    const { deviceIdentifier, deviceType, owner, t } = this.props;
    this.setState({ loading: true });

    // get current page
    const currentPage =
      params.hasOwnProperty('page') && params.page ? params.page : 1;

    let limit = 10;
    if (params.results) {
      limit = params.results;
    }

    const extraParams = {
      offset: limit * (currentPage - 1), // calculate the offset
      limit: limit,
    };

    let encodedExtraParams = Object.keys(extraParams)
      .map(key => key + '=' + extraParams[key])
      .join('&');

    // const paramSet = encodedExtraParams;
    if (filters !== '') {
      encodedExtraParams = encodedExtraParams + filters;
    }
    axios
      .get(
        window.location.origin +
          this.config.serverConfig.invoker.uri +
          this.config.serverConfig.invoker.deviceMgt +
          `/devices/${deviceType}/${deviceIdentifier}/operations?owner=${owner}&` +
          encodedExtraParams,
      )
      .then(res => {
        if (res.status === 200) {
          const pagination = { ...this.state.pagination };
          this.setState({
            operations: res.data.data,
            pagination,
            loading: false,
          });
        }
      })
      .catch(error => {
        handleApiError(error, t('api_getOperationError'), t);
      });
  };

  updateOperationState = (operationId, status) => {
    let operation = this.state.operations.operations.find(
      operation => operation.id === operationId,
    );
    const operationObject = {
      operationId: operationId,
      status: status,
      operationCode: operation.code,
    };
    const { deviceType, deviceIdentifier, t } = this.props;
    axios
      .put(
        window.location.origin +
          this.config.serverConfig.invoker.uri +
          this.config.serverConfig.invoker.deviceMgt +
          `/devices/${deviceType}/${deviceIdentifier}/operation`,
        operationObject,
      )
      .then(res => {
        if (res.status === 200) {
          notification.success({
            message: t('api_successMsg'),
            duration: 5,
            description: t('api_operationUpdatedMsg'),
          });
          const pagination = this.state.pagination;
          this.getDeviceOperations({
            page: pagination.current,
          });
        }
      })
      .catch(error => {
        handleApiError(error, t('api_getOperationError'), t);
      });
  };

  handleTableChange = (pagination, filters, sorter) => {
    const pager = { ...this.state.pagination };
    pager.current = pagination.current;
    pager.pageSize = pagination.pageSize;
    this.setState({
      pagination: pager,
    });
    this.getDeviceOperations(
      {
        results: pagination.pageSize,
        page: pagination.current,
        sortField: sorter.field,
        sortOrder: sorter.order,
        ...filters,
      },
      this.filterValues,
    );
  };

  showModal = () => {
    this.setState({
      isModalVisible: true,
    });
  };

  handleCancel = () => {
    this.setState({
      isModalVisible: false,
    });
  };

  // create UI component with activity details in expanded row
  getDetailsUI = responses => {
    const { t } = this.props;
    if (Object.keys(responses[0]).length === 0) {
      return (
        <Empty
          className={styles.customEmpty}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }
    return (
      <div>
        {responses.map((response, index) => {
          if (!response.response) {
            return (
              <Empty
                className={styles.customEmpty}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                key={index}
              />
            );
          }
          try {
            let resObject = JSON.parse(response.response);
            return (
              <div key={index}>
                <Descriptions
                  column={1}
                  size="small"
                  bordered={true}
                  className={styles.formattedTableItems}
                >
                  {Object.keys(resObject).map((key, i) => {
                    if (i > 3) {
                      return;
                    }
                    return (
                      <Descriptions.Item label={key} key={key}>
                        <p>
                          {typeof resObject[key] === 'object'
                            ? JSON.stringify(resObject[key])
                            : resObject[key]}
                        </p>
                      </Descriptions.Item>
                    );
                  })}
                  {Object.keys(resObject).length > 3 && (
                    <Descriptions.Item label={'...'}>
                      <p>...</p>
                    </Descriptions.Item>
                  )}
                </Descriptions>
                {Object.keys(resObject).length > 3 && (
                  <div className={styles.viewAllButton}>
                    <Button type="dashed" size="small" onClick={this.showModal}>
                      {t('label_viewAll')}
                    </Button>
                  </div>
                )}
                <Modal
                  title={t('label_operationDetails')}
                  className={styles.operationDetailsModal}
                  open={this.state.isModalVisible}
                  onCancel={this.handleCancel}
                  footer={null}
                  width={800}
                  maskClosable={false}
                >
                  <Tabs defaultActiveKey="formatted">
                    <TabPane
                      tab={
                        <span>
                          <BarsOutlined />
                          {t('label_formatted')}
                        </span>
                      }
                      key="formatted"
                    >
                      <Descriptions
                        column={1}
                        size="small"
                        bordered={true}
                        className={styles.formattedItems}
                      >
                        {Object.keys(resObject).map(key => (
                          <Descriptions.Item label={key} key={key}>
                            {typeof resObject[key] === 'object'
                              ? JSON.stringify(resObject[key])
                              : resObject[key]}
                          </Descriptions.Item>
                        ))}
                      </Descriptions>
                    </TabPane>
                    <TabPane
                      tab={
                        <span>
                          <CodeOutlined />
                          {t('label_raw')}
                        </span>
                      }
                      key="raw"
                    >
                      <JSONPretty
                        id="json-pretty"
                        theme={jsonTheme}
                        data={resObject}
                      />
                    </TabPane>
                  </Tabs>
                </Modal>
              </div>
            );
          } catch (e) {
            return (
              <Alert
                showIcon={false}
                message={response.response}
                key={response.response}
                banner
              />
            );
          }
        })}
      </div>
    );
  };

  refreshOperationLog = () => {
    const { pagination } = this.state;
    this.getDeviceOperations({
      page: pagination.current,
    });
  };

  render() {
    const { operations, pagination, loading } = this.state;
    const { t } = this.props;
    return (
      <div>
        <div>
          <div className={styles.table}>
            <Collapse
              bordered={false}
              expandIcon={() => <SearchOutlined />}
              className={styles.searchCollapse}
            >
              <Panel
                header={t('form_searchOperationLogs')}
                key="1"
                style={{ borderColor: '#fff' }}
              >
                <Filter
                  filters={this.state.searchFields}
                  callback={(params, values) =>
                    this.fetchOperationFilters({}, values)
                  }
                />
              </Panel>
            </Collapse>
          </div>
        </div>
        <div
          className={styles.tableContainer}
          style={{ backgroundColor: '#ffffff', borderRadius: 5 }}
        >
          <Col span={4} offset={22}>
            <Tooltip
              placement="bottom"
              title={t('label_refreshOperationLog')}
              autoAdjustOverflow={true}
            >
              <Button
                type="link"
                shape="circle"
                icon={<ReloadOutlined />}
                onClick={this.getDeviceOperations}
                size={'default'}
                style={{ margin: '2px' }}
              >
                {t('label_refresh')}
              </Button>
            </Tooltip>
          </Col>
          {loading && <Spin />}
          {!loading && (
            <Table
              rowKey={'id'}
              dataSource={operations.operations}
              columns={this.columns}
              pagination={{
                ...pagination,
                size: 'small',
                total: operations.count,
                disabled: loading,
                showTotal: (total, range) =>
                  t('table_pagination', {
                    range1: range[0],
                    range2: range[1],
                    total: total,
                    data: t('label_operations'),
                  }),
              }}
              onChange={this.handleTableChange}
              loading={loading}
              expandable={{
                expandedRowRender: record => (
                  <span style={{ background: '#ffffff' }}>
                    <Col span={18} offset={3}>
                      {record.responses && (
                        <div>{this.getDetailsUI(record.responses)}</div>
                      )}
                    </Col>
                  </span>
                ),

                rowExpandable: record => record.name !== 'Not Expandable',
                expandIconColumnIndex: 4,
                expandIcon: ({ expanded, onExpand, record }) =>
                  expanded ? (
                    <Tooltip placement="top" title={t('label_cancel')}>
                      <Button
                        type="link"
                        size="small"
                        onClick={e => onExpand(record, e)}
                      >
                        {t('label_hideDetails')}
                      </Button>
                    </Tooltip>
                  ) : (
                    <Tooltip placement="top" title={t('label_view')}>
                      <Button
                        type="link"
                        size="small"
                        onClick={
                          !record.hasOwnProperty('responses')
                            ? e => {
                                onExpand(record, e);
                                this.modifyOperationData(record);
                              }
                            : e => onExpand(record, e)
                        }
                      >
                        {t('label_details')}
                      </Button>
                    </Tooltip>
                  ),
              }}
            />
          )}
        </div>
      </div>
    );
  }
}

export default withConfigContext(withTranslation()(DeviceOperationLog));
